import { Search, Edit2, Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

const MAX_TITLES = 5;
const MAX_DESCRIPTIONS = 3;
const TITLE_MAX_CHARS = 30;
const DESC_MAX_CHARS = 90;

const GoogleSearchPreview = ({ title, url, description, keywords, compact = false, hideKeywords = false, onChange, titles: propTitles, descriptions: propDescriptions, onValidationChange }) => {
  const [localTitle, setLocalTitle] = useState(title || 'Título del anuncio');
  const [localUrl, setLocalUrl] = useState(url || 'https://www.ejemplo.com/landing');
  const [localDescription, setLocalDescription] = useState(description || 'Descripción del anuncio');
  const initialKeywords = Array.isArray(keywords) ? keywords : (keywords ? String(keywords).split(',').map(k => k.trim()).filter(Boolean) : []);
  const [localKeywords, setLocalKeywords] = useState(initialKeywords);
  const [isEditingText, setIsEditingText] = useState(false);

  // Listas completas para modal - usar props si están disponibles, sino usar valores locales
  const [titles, setTitles] = useState(propTitles && propTitles.length > 0 ? propTitles : [localTitle]);
  const [descriptions, setDescriptions] = useState(propDescriptions && propDescriptions.length > 0 ? propDescriptions : [localDescription]);

  const addKeyword = (kw) => {
    const value = kw.trim();
    if (!value) return;
    if (localKeywords.includes(value)) return;
    const next = [...localKeywords, value];
    setLocalKeywords(next);
    onChange && onChange({ keywords: next });
  };

  const removeKeyword = (kw) => {
    const next = localKeywords.filter(k => k !== kw);
    setLocalKeywords(next);
    onChange && onChange({ keywords: next });
  };

  const handleSaveTexts = () => {
    const cleanTitles = titles.map(t => t.trim()).filter(Boolean).slice(0, MAX_TITLES);
    const cleanDescs = descriptions.map(d => d.trim()).filter(Boolean).slice(0, MAX_DESCRIPTIONS);
    const firstTitle = cleanTitles[0] || localTitle;
    const firstDesc = cleanDescs[0] || localDescription;
    setLocalTitle(firstTitle);
    setLocalDescription(firstDesc);
    setIsEditingText(false);
    onChange && onChange({
      title: firstTitle,
      description: firstDesc,
      titles: cleanTitles,
      descriptions: cleanDescs
    });
  };

  const padding = compact ? 'p-3' : 'p-4';

  const canAddTitle = useMemo(() => titles.length < MAX_TITLES, [titles.length]);
  const canAddDesc = useMemo(() => descriptions.length < MAX_DESCRIPTIONS, [descriptions.length]);

  // Validación de límites de caracteres
  const isTextValid = useMemo(() => {
    const allTitles = [localTitle, ...titles].filter(Boolean);
    const allDescriptions = [localDescription, ...descriptions].filter(Boolean);
    
    const titlesExceedLimit = allTitles.some(title => title.length > TITLE_MAX_CHARS);
    const descriptionsExceedLimit = allDescriptions.some(desc => desc.length > DESC_MAX_CHARS);
    
    return !titlesExceedLimit && !descriptionsExceedLimit;
  }, [localTitle, localDescription, titles, descriptions]);

  // Notificar cambios de validación al componente padre
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isTextValid);
    }
  }, [isTextValid, onValidationChange]);

  return (
    <div className={`bg-white ${padding} rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition-all duration-200`}> 
      {!hideKeywords && (
        <div className={`mb-3 border border-gray-200 rounded-xl ${compact ? 'p-2' : 'p-3'} shadow-sm bg-white`}> 
          <div className="flex items-center gap-2 mb-2">
            <Search className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-gray-500`} />
            <span className="text-xs text-gray-600">Palabras clave</span>
          </div>
          
          {/* Chips en múltiples filas */}
          <div className="flex flex-wrap gap-1 mb-2 min-h-[24px]">
            {localKeywords.map((kw) => (
              <span key={kw} className="text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full whitespace-nowrap inline-flex items-center gap-1">
                {kw}
                <button className="text-gray-400 hover:text-gray-600" onClick={() => removeKeyword(kw)} aria-label={`Quitar ${kw}`}>×</button>
              </span>
            ))}
          </div>
          
          {/* Input para agregar */}
          <input
            type="text"
            placeholder="Agregar palabra clave y Enter"
            className={`w-full outline-none text-xs border border-gray-200 rounded-md px-2 py-1 ${compact ? 'py-0.5' : 'py-1.5'}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addKeyword(e.currentTarget.value);
                e.currentTarget.value = '';
              }
            }}
          />
        </div>
      )}

      <div className="mb-1 inline-block text-[10px] px-2 py-0.5 bg-primary bg-opacity-10 text-primary rounded-sm">
        Ad
      </div>

      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <input
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              className="w-full text-primary text-base sm:text-lg leading-tight hover:underline outline-none"
              maxLength={TITLE_MAX_CHARS}
            />
            <input
              value={localUrl}
              onChange={(e) => setLocalUrl(e.target.value)}
              className="w-full text-green-600 text-xs sm:text-sm mb-1 outline-none"
            />
            <textarea
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              className="w-full text-xs sm:text-sm text-gray-700 outline-none resize-none leading-snug"
              rows={compact ? 2 : 3}
              maxLength={DESC_MAX_CHARS}
            />
          </div>
          <button
            className={`flex-shrink-0 bg-gray-50 hover:bg-gray-100 rounded-md p-2 border transition-colors ${
              !isTextValid 
                ? 'text-red-500 hover:text-red-600 border-red-200 bg-red-50 hover:bg-red-100' 
                : 'text-gray-500 hover:text-primary border-gray-200'
            }`}
            title={!isTextValid ? "🙈 Ups… ¡la IA se emocionó escribiendo! Editá tus textos para que entren en el límite." : "Editar títulos y descripciones"}
            onClick={() => setIsEditingText(true)}
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isEditingText && (
        <div className="fixed inset-0 modal-safe-area bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-4xl p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Editar recursos de texto</h3>
                <p className="text-xs text-gray-500">Hasta {MAX_TITLES} títulos (máx. {TITLE_MAX_CHARS} caracteres) y {MAX_DESCRIPTIONS} descripciones (máx. {DESC_MAX_CHARS}).</p>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setIsEditingText(false)}>×</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Títulos */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium text-gray-700">Títulos ({titles.length}/{MAX_TITLES})</div>
                  {canAddTitle && (
                    <button className="text-xs text-primary flex items-center gap-1" onClick={() => setTitles([...titles, ''])}>
                      <Plus className="w-3 h-3" /> Agregar
                    </button>
                  )}
                </div>
                <div className="space-y-3 max-h-64 overflow-auto pr-1">
                  {titles.map((t, idx) => (
                    <div className="flex items-center gap-2" key={`t-${idx}`}>
                      <input
                        value={t}
                        maxLength={TITLE_MAX_CHARS}
                        onChange={(e) => {
                          const next = [...titles];
                          next[idx] = e.target.value;
                          setTitles(next);
                        }}
                        className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm"
                      />
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-[10px] ${(t || '').length > TITLE_MAX_CHARS ? 'text-red-500' : 'text-gray-500'}`}>
                          {(t || '').length}/{TITLE_MAX_CHARS}
                        </span>
                        {titles.length > 1 && (
                          <button className="text-gray-400 hover:text-red-600" onClick={() => setTitles(titles.filter((_, i) => i !== idx))} aria-label="Eliminar título">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Descripciones */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium text-gray-700">Descripciones ({descriptions.length}/{MAX_DESCRIPTIONS})</div>
                  {canAddDesc && (
                    <button className="text-xs text-primary flex items-center gap-1" onClick={() => setDescriptions([...descriptions, ''])}>
                      <Plus className="w-3 h-3" /> Agregar
                    </button>
                  )}
                </div>
                <div className="space-y-3 max-h-64 overflow-auto pr-1">
                  {descriptions.map((d, idx) => (
                    <div className="flex items-start gap-2" key={`d-${idx}`}>
                      <textarea
                        value={d}
                        rows={2}
                        maxLength={DESC_MAX_CHARS}
                        onChange={(e) => {
                          const next = [...descriptions];
                          next[idx] = e.target.value;
                          setDescriptions(next);
                        }}
                        className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm resize-none"
                      />
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-[10px] ${(d || '').length > DESC_MAX_CHARS ? 'text-red-500' : 'text-gray-500'}`}>
                          {(d || '').length}/{DESC_MAX_CHARS}
                        </span>
                        {descriptions.length > 1 && (
                          <button className="text-gray-400 hover:text-red-600" onClick={() => setDescriptions(descriptions.filter((_, i) => i !== idx))} aria-label="Eliminar descripción">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-3">
              <button className="px-4 py-2 text-sm bg-primary text-white rounded-md" onClick={handleSaveTexts}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleSearchPreview;
